import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from './components/ui/sonner';
import { LanguageProvider } from './hooks/useLanguage';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import PricingPage from './pages/PricingPage';
import ResourcesPage from './pages/ResourcesPage';
import BlogPostPage from './pages/BlogPostPage';
import ContactPage from './pages/ContactPage';
import BookingPage from './pages/BookingPage';
import TrademarkServicesPage from './pages/TrademarkServicesPage';
import GovernmentContractsPage from './pages/GovernmentContractsPage';
import FamilyLegalPlanningPage from './pages/FamilyLegalPlanningPage';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import PublicFormPage from './pages/PublicFormPage';
import SEO from './components/SEO';

// Privacy Policy Page
const PrivacyPage = () => (
  <Layout>
    <SEO 
      title="Privacy Policy"
      description="Privacy Policy for J. Brantley Law, PLLC. Learn how we collect, use, and protect your personal information."
    />
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="privacy-page">
      <h1 className="text-4xl font-serif text-[#1A1A1A] mb-8">Privacy Policy</h1>
      <div className="prose prose-lg text-[#595959]">
        <p className="mb-6">Last Updated: January 2024</p>
        
        <h2 className="text-2xl font-serif text-[#1A1A1A] mt-8 mb-4">Information We Collect</h2>
        <p className="mb-4">We collect information you provide directly to us, such as when you fill out a contact form, book a consultation, or communicate with us. This may include your name, email address, phone number, and any other information you choose to provide.</p>
        
        <h2 className="text-2xl font-serif text-[#1A1A1A] mt-8 mb-4">How We Use Your Information</h2>
        <p className="mb-4">We use the information we collect to:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>Respond to your inquiries and provide legal services</li>
          <li>Schedule and manage consultations</li>
          <li>Send you information about our services</li>
          <li>Improve our website and services</li>
        </ul>
        
        <h2 className="text-2xl font-serif text-[#1A1A1A] mt-8 mb-4">Information Sharing</h2>
        <p className="mb-4">We do not sell, trade, or otherwise transfer your personal information to outside parties except as required by law or to provide our legal services.</p>
        
        <h2 className="text-2xl font-serif text-[#1A1A1A] mt-8 mb-4">Contact Us</h2>
        <p className="mb-4">If you have questions about this Privacy Policy, please contact us at jbrantley@jenniferbrantleylaw.com.</p>
      </div>
    </div>
  </Layout>
);

// Terms of Service Page
const TermsPage = () => (
  <Layout>
    <SEO 
      title="Terms of Service"
      description="Terms of Service for J. Brantley Law, PLLC. Important legal disclaimers about attorney-client relationships and use of this website."
    />
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="terms-page">
      <h1 className="text-4xl font-serif text-[#1A1A1A] mb-8">Terms of Service</h1>
      <div className="prose prose-lg text-[#595959]">
        <p className="mb-6">Last Updated: January 2024</p>
        
        <h2 className="text-2xl font-serif text-[#1A1A1A] mt-8 mb-4">Legal Disclaimer</h2>
        <p className="mb-4">The information provided on this website is for general informational purposes only and does not constitute legal advice. No attorney-client relationship is formed by your use of this website or by contacting our firm through this website.</p>
        
        <h2 className="text-2xl font-serif text-[#1A1A1A] mt-8 mb-4">No Guarantees</h2>
        <p className="mb-4">Prior results do not guarantee a similar outcome. Every legal matter is different, and results depend on the specific facts and circumstances of each case.</p>
        
        <h2 className="text-2xl font-serif text-[#1A1A1A] mt-8 mb-4">Attorney-Client Relationship</h2>
        <p className="mb-4">An attorney-client relationship with J. Brantley Law, PLLC is only formed after a formal engagement agreement has been signed by both parties. Submitting information through this website does not create an attorney-client relationship.</p>
        
        <h2 className="text-2xl font-serif text-[#1A1A1A] mt-8 mb-4">Confidentiality</h2>
        <p className="mb-4">Please do not send confidential or sensitive information through this website. Information submitted through contact forms is not protected by attorney-client privilege until an engagement has been established.</p>
        
        <h2 className="text-2xl font-serif text-[#1A1A1A] mt-8 mb-4">Licensing</h2>
        <p className="mb-4">Jennifer N. Brantley is licensed to practice law in Georgia and Texas. Information on this website may not apply to your jurisdiction.</p>
        
        <h2 className="text-2xl font-serif text-[#1A1A1A] mt-8 mb-4">Contact</h2>
        <p className="mb-4">For questions about these terms, please contact us at jbrantley@jenniferbrantleylaw.com.</p>
      </div>
    </div>
  </Layout>
);

function App() {
  return (
    <LanguageProvider>
      <div className="App">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Layout><HomePage /></Layout>} />
          <Route path="/about" element={<Layout><AboutPage /></Layout>} />
          <Route path="/services" element={<Layout><ServicesPage /></Layout>} />
          <Route path="/services/:slug" element={<Layout><ServicesPage /></Layout>} />
          <Route path="/trademarks" element={<Layout><TrademarkServicesPage /></Layout>} />
          <Route path="/government-contracts" element={<Layout><GovernmentContractsPage /></Layout>} />
          <Route path="/family-legal-planning" element={<Layout><FamilyLegalPlanningPage /></Layout>} />
          <Route path="/pricing" element={<Layout><PricingPage /></Layout>} />
          <Route path="/resources" element={<Layout><ResourcesPage /></Layout>} />
          <Route path="/resources/:slug" element={<Layout><BlogPostPage /></Layout>} />
          <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
          <Route path="/book" element={<Layout><BookingPage /></Layout>} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          
          {/* Dynamic Form Routes */}
          <Route path="/forms/:slug" element={<Layout><PublicFormPage /></Layout>} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          </Routes>
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
